import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidUrl } from "../../utils/validation";
import { message } from "antd";
import { BannerService } from "../../services/bannerService";
import { BannerDetail, BannerFormData, BannerListItem, BannerOrderItem, defaultBannerFormData, OperationStatus } from "./constants";


interface BannerContextState {
	// Core Domain Data
	bannerList: BannerListItem[];
	bannerDetail: BannerDetail | null;
	bannerForm: BannerFormData;
	operationStatus: OperationStatus;

	// Setters
	setBannerForm: React.Dispatch<React.SetStateAction<BannerFormData>>;
    clearBannerFormData: () => void;

	// Actions
	addBanner: () => Promise<void>;
	getBannerList: () => Promise<void>;
	updateBanner: () => Promise<void>;
    removeBanner: (id: string) => Promise<void>;
	getBannerDetails: (id: string) => Promise<void>;
    updateBannerOrders: (orderedList: BannerOrderItem[]) => Promise<void>;
}

interface BannerProviderProps {
    children: React.ReactNode;
}

const BannerContext = createContext<BannerContextState | undefined>(
	undefined
);

export const useBannerContext = () => {
	const context = useContext(BannerContext);
	if (!context) {
		throw new Error(
			"useBannerContext must be used within a BannerContextProvider"
		);
	}
	return context;
};

export const BannerContextProvider: React.FC<BannerProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [bannerList, setBannerList] = useState<BannerListItem[]>([]);
	const [bannerDetail, setBannerDetail] = useState<BannerDetail | null>(null);
	const [bannerForm, setBannerForm] = useState<BannerFormData>(defaultBannerFormData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isBannerDataValid = (data: BannerFormData) => {
		if (!data.bannerLocalizable || data.bannerLocalizable.length === 0) {
			return false;
		}

		for (const localizable of data.bannerLocalizable) {
			if (!localizable.firstHeaderName || !localizable.headerName || !localizable.culture) {
				return false;
			}
		}
		return true;
	};

	const clearBannerFormData = () => {
		setBannerForm(defaultBannerFormData);
		setBannerDetail(null);
	};

	const addBanner = async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isBannerDataValid(bannerForm) || !bannerForm.image) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerForm.bannerLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerService.addBanner(bannerForm);

			if (response.data?.result?.isSuccessful) {
				message.success("Banner başarıyla eklendi.");
				setBannerForm(defaultBannerFormData);
				navigate("/banners");
			} else {
				message.error(response.data.result?.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getBannerDetails = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerService.getBannerDetails(id);

			if (response.isSuccessful) {
				setBannerDetail({
					bannerId: response.result.bannerId,
					displayOrder: response.result.displayOrder,
					bannerLocalizable: response.result.bannerLocalizable,
					imageList: {
						imageUrl: response.result.imageUrl,
						smallImageUrl: response.result.smallImageUrl,
						mobileImageUrl: response.result.mobileImageUrl,
					}
				});

				setBannerForm(prevState => ({
					...prevState,
					bannerId: response.result.bannerId,
					bannerLocalizable: response.result.bannerLocalizable,
				}));
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateBanner = async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!isBannerDataValid(bannerForm) || !bannerForm.bannerId) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const localizableChanged = JSON.stringify(bannerForm.bannerLocalizable) !== JSON.stringify(bannerDetail?.bannerLocalizable);
		const imagesChanged = bannerForm.image || bannerForm.smallImage || bannerForm.mobileImage;

		if (!localizableChanged && !imagesChanged) {
			message.info("Değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerForm.bannerLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerService.updateBanner(bannerForm);

			if (response.data.isSuccessful) {
				message.success("Banner başarıyla güncellendi.");
				setBannerForm(defaultBannerFormData);
				setBannerDetail(null);
				navigate("/banners");
			} else {
				message.error(response.data.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const getBannerList = async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerService.getBannerList();

			if (response.isSuccessful) {
				setBannerList(response.result?.items || []);
				clearBannerFormData();
			} else {
				message.error(response.error ? response.error : "Banner listesi yüklenirken hata oluştu.");
				setBannerList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Banner listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const removeBanner = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await BannerService.removeBanner(id);
			if (response.data?.result?.isSuccessful) {
				message.success("Banner başarıyla silindi.");
				await getBannerList();
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Banner silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateBannerOrders = async (orderedList: BannerOrderItem[]) => {
		setOperationStatus({ type: "loading", operation: "update" });
		try {
			const response = await BannerService.updateBannerOrders(orderedList);
			if (response.data?.result?.isSuccessful) {
				message.success("Banner sıralaması güncellendi.");
				navigate("/banners");
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Banner sıralamaları güncellenirken bir hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	return (
		<BannerContext.Provider
			value={{
				bannerForm,
				bannerList,
				bannerDetail,
				setBannerForm,
				operationStatus,
				addBanner,
				removeBanner,
				updateBannerOrders,
				getBannerList,
				getBannerDetails,
				clearBannerFormData,
				updateBanner,
			}}
		>
			{children}
		</BannerContext.Provider>
	);
};
